// Result-page helpers: build per-focus-type text blocks from wizard data
// and compute their proportional weights.

import { WIZARD_STORAGE_KEY } from "@/lib/wizardConfig";

export const RESULT_TYPES = ["cat", "owl", "bee", "dog"];

export const RESULT_META = {
  cat: {
    key: "cat",
    name: "Katze",
    emoji: "😸",
    label: "😸 Katze — emotional abholen",
    accentHex: "#10B981",
    barClass: "bg-emerald-500",
    bg: "#F0FDF4",
    border: "border-emerald-100",
    btnClass:
      "bg-emerald-500 hover:bg-emerald-600 text-white",
    iterateLabel: "Stärkere Katze",
  },
  owl: {
    key: "owl",
    name: "Eule",
    emoji: "🦉",
    label: "🦉 Eule — Sinn stiften",
    accentHex: "#7A92D4",
    barClass: "bg-blue-500",
    bg: "#EFF6FF",
    border: "border-blue-100",
    btnClass: "bg-blue-500 hover:bg-blue-600 text-white",
    iterateLabel: "Mehr Eule",
  },
  bee: {
    key: "bee",
    name: "Biene",
    emoji: "🐝",
    label: "🐝 Biene — Umsetzung",
    accentHex: "#F3651D",
    barClass: "bg-amber-500",
    bg: "#FEF3C7",
    border: "border-amber-100",
    btnClass: "bg-amber-500 hover:bg-amber-600 text-white",
    iterateLabel: "Klarere Biene",
  },
  dog: {
    key: "dog",
    name: "Hund",
    emoji: "🐕",
    label: "🐕 Hund — Konsequenz",
    accentHex: "#EF4444",
    barClass: "bg-red-500",
    bg: "#FEF2F2",
    border: "border-red-100",
    btnClass: "bg-red-500 hover:bg-red-600 text-white",
    iterateLabel: "Härterer Hund",
  },
};

function clean(s) {
  return (s || "").trim();
}

export function buildBlockText(type, data) {
  const d = data || {};
  if (type === "cat") {
    const audience = clean(d.audience) || "deinen Empfängerkreis";
    const benefit =
      clean(d.emotionalBenefit) ||
      "weniger Hin-und-Her und klarere Spielführung";
    const options =
      clean(d.options) || "Du entscheidest, welcher Weg für dich passt.";
    const tone = clean(d.tone);
    const toneNote = tone
      ? ` Ich weiß, dass dabei der Ton zählt: ${tone}.`
      : "";
    return (
      `Ich weiß, dass dieses Thema bei ${audience} Emotionen auslösen kann — und mir liegt daran, dass es sich gut anfühlt. ` +
      `Was du persönlich davon hast: ${benefit}.${toneNote} ` +
      `Du behältst die Wahl. ${options}`
    );
  }

  if (type === "owl") {
    const bigPicture =
      clean(d.bigPicture) || "wir stehen vor einer wichtigen Weichenstellung";
    const facts =
      clean(d.facts) || "die aktuellen Daten zeigen klar, dass Handeln Wirkung hat";
    const relevance =
      clean(d.relevance) || "die nächsten Wochen entscheiden über den Ausgang";
    return (
      `Im größeren Zusammenhang: ${bigPicture}. ` +
      `Daten und Fakten: ${facts}. ` +
      `Genau deshalb ist das jetzt relevant: ${relevance}.`
    );
  }

  if (type === "bee") {
    const todos = clean(d.todos) || "1. Vorbereiten  2. Umsetzen  3. Prüfen";
    const watch =
      clean(d.watchOuts) || "saubere Dokumentation und klare Verantwortlichkeiten";
    const steps =
      clean(d.steps) ||
      "1. Anfrage einreichen  2. Approval einholen  3. Versand auslösen";
    const signals =
      clean(d.successSignals) ||
      "alle Stakeholder bestätigen den Status und keine Eskalation kommt zurück";
    return (
      `Konkret zu tun:\n${todos}\n\n` +
      `Bitte besonders achten auf: ${watch}.\n\n` +
      `Schritt für Schritt:\n${steps}\n\n` +
      `Du erkennst, dass alles richtig läuft, wenn ${signals}.`
    );
  }

  // dog
  const personal =
    clean(d.personalConsequence) ||
    "du gewinnst Klarheit und kannst schneller agieren";
  const ifThen =
    clean(d.ifThen) ||
    "Wenn du es tust, ziehst du das Thema sauber durch. Wenn nicht, verlängert sich der Schwebezustand.";
  const others =
    clean(d.consequencesForOthers) ||
    "Auch dein Team profitiert von einer klaren Entscheidung.";
  const next =
    clean(d.nextAction) ||
    "Eine kurze Bestätigung bis Ende der Woche genügt.";
  return (
    `Für dich persönlich bedeutet das: ${personal}. ` +
    `${ifThen} ` +
    `${others} ` +
    `Nächster Schritt — jetzt: ${next}`
  );
}

export function buildInitialBlocks(wizardData) {
  return Object.fromEntries(
    RESULT_TYPES.map((t) => [t, buildBlockText(t, wizardData)])
  );
}

export function computeWeights(blocks) {
  const lengths = Object.fromEntries(
    RESULT_TYPES.map((t) => [t, (blocks[t] || "").length])
  );
  const total = Object.values(lengths).reduce((a, b) => a + b, 0) || 1;
  return Object.fromEntries(
    RESULT_TYPES.map((t) => [t, Math.round((lengths[t] / total) * 100)])
  );
}

export function buildFullMessage(blocks) {
  return RESULT_TYPES.map((t) => {
    const m = RESULT_META[t];
    return `${m.emoji} ${m.name.toUpperCase()}\n${blocks[t]}`;
  }).join("\n\n");
}

export function loadWizardDraft() {
  try {
    const raw = localStorage.getItem(WIZARD_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.data || null;
  } catch (e) {
    return null;
  }
}
